import { AfterViewInit, Component, Inject, Input, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Popup, StoryblokAudience, PopupCloseConfig, PopupShowConfig } from '../../_model/storyblock';
import { AuthService } from '../../_service/AuthService';
import { CmsService } from '../../_service/CmsService';
import { WINDOW } from '../../_service/WindowService';
import { NgbModalDefaultOptions } from '../../_theme/snackbarformats';
import { CmsSectionComponent } from '../cms-section/cms-section.component';
import { NgClass } from '@angular/common';
import { CmsDirective } from '../../appModule/cms.directive';
import { LocalstorageService } from 'src/_service/LocalstorageService';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/_environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { GlobalService } from 'src/_service/GlobalService';

const DEFAULT_SHOWAFTERTIME = 7000;

@Component({
  selector: 'app-cms-popup',
  templateUrl: './cms-popup.component.html',
  styleUrl: './cms-popup.component.scss',
  imports: [
    NgClass,
    CmsDirective,
    CmsSectionComponent
  ],
  host: { ngSkipHydration: 'true' }
})
export class CmsPopupComponent implements AfterViewInit {

  dismissedPopups: { id: string, dismissedAt: Date }[] = [];

  popupTitle: any;
  popupOptions = NgbModalDefaultOptions;
  popupModal: NgbModalRef;

  backgroundColor = "bg-white";

  private _popup: Popup
  @Input() set popup(value: Popup) {
    this._popup = value;
    if (value.title)
      this.popupTitle = this._cmsService.render_rt(value.title)

    this.popupOptions.size = value.size;
    this.backgroundColor = value.bgcolor ? 'bg-' + value.bgcolor : "bg-white"
  }
  get popup() { return this._popup }

  private maxScroll = 0;
  private idleTimer: any;

  private scrollListener: () => void = () => { };
  private mouseoutListener: () => void = () => { };
  private touchListener: () => void = () => { };
  private clickListener: () => void = () => { };
  private mousemoveListener: () => void = () => { };


  @ViewChild('modalcontent', { read: TemplateRef }) modalContent: TemplateRef<any>;

  private _unsubscribe = new Subject<void>();

  constructor(
    private renderer2: Renderer2,
    private _authService: AuthService,
    private _cmsService: CmsService,
    private _modalService: NgbModal,
    private _localstorage: LocalstorageService,
    private _router: Router,
    private _globalService: GlobalService,
    @Inject(WINDOW) private window: Window,
  ) { }

  ngAfterViewInit() {

    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this._unsubscribe)
    ).subscribe(() => {
      if (this.popupModal) {
        this.popupModal.close();
      }
    });
    this.dismissedPopups = this._localstorage.getObject("dismissedpopups") || [];

    if (
      this.popup &&
      !this.popup.hidden &&
      (
        (this._authService.isAuthenticatedValue && this.popup.audience === StoryblokAudience.CUSTOMERS)
        || (!this._authService.isAuthenticatedValue && this.popup.audience === StoryblokAudience.NON_CUSTOMERS)
        || this.popup.audience === StoryblokAudience.EVERYONE
      ) && !this.dismissedPopups.map(f => f.id).includes(this.popup._uid) //makes sure that the modal isnt dismissed before

    ) {

      switch (this.popup.showconfig) {
        case (PopupShowConfig.IMMEDIATELY):
          this.openPopup()
          break;
        case (PopupShowConfig.TIMER):
          setTimeout(() => {
            this.openPopup()
          }, this.popup.showaftertime | DEFAULT_SHOWAFTERTIME);
          break;
        case (PopupShowConfig.MOUSELEAVE_AND_IDLETIMER):
          this.mouseoutListener = this.renderer2.listen("body", "mouseleave", event => {
            this.openPopup();
          })
          this.scrollListener = this.renderer2.listen("document", "scroll", event => { this.resetIdleTimer() })
          this.touchListener = this.renderer2.listen("document", "touchstart", event => { this.resetIdleTimer() })
          this.clickListener = this.renderer2.listen("document", "click", event => { this.resetIdleTimer() })
          this.mousemoveListener = this.renderer2.listen("document", "mousemove", event => { this.resetIdleTimer() })

          this.resetIdleTimer();

          break;
        default:
          this.scrollListener = this.renderer2.listen("document", "scroll", event => {
            this.maxScroll = this.window.scrollY > this.maxScroll ? this.window.scrollY : this.maxScroll;
            if (this.maxScroll - this.window.scrollY > 250) {
              this.openPopup();
            }
          })
      }
    }
  }

  ngOnDestroy() {
    if (this.popupModal) 
      this.popupModal.close();
    
    this.removeListeners();
    clearTimeout(this.idleTimer);
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  openPopup() {
    //makes sure only 1 modal is added
    if (!this._globalService.isInBrowser) return;
    if (this.popupModal) { return; }

    if (this.popup.closeconfig === PopupCloseConfig.CLOSEBUTTON)
      this.popupOptions.beforeDismiss = () => { return false; }

    this.popupModal = this._modalService.open(this.modalContent, this.popupOptions)
    this.popupModal.closed.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
      this.setPopupDismissed()
    })
    this.popupModal.dismissed.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
      this.setPopupDismissed()
    })

    this.removeListeners();
    clearTimeout(this.idleTimer);
  }

  setPopupDismissed() {
    if (!this.popup) return;

    this.dismissedPopups.push({ id: this.popup._uid, dismissedAt: new Date() })
    if (environment.production)
      this._localstorage.setObject("dismissedpopups", this.dismissedPopups)
  }

  resetIdleTimer() {
    clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => {
      this.openPopup()
    }, this.popup?.showaftertime | DEFAULT_SHOWAFTERTIME);
  }

  removeListeners() {
    this.scrollListener(); //removes scrolllistener
    this.mouseoutListener(); //removes mouseoutlistener

    this.touchListener()
    this.clickListener()
    this.mousemoveListener()
  }

  get_content(field) {
    if (!field || field.length === 0) return null;
    // abstract field render can be used for content from different (CMS) sources
    if (!!field.text) return field.text;
    // no extra work need
    return field;
  }
}
